import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Button,
  Card,
  CardBody,
  Checkbox,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Spinner,
  Switch,
} from '@nextui-org/react';
import {
  ArrowLeft,
  CheckCircle2,
  Handshake,
  Image as ImageIcon,
  Images,
  Link2,
  Pencil,
  Plus,
  Trash2,
  Upload,
  XCircle,
} from 'lucide-react';
import adminSlidersService, {
  AdminSlide,
  AdminSlider,
  AdminSponsor,
  SlideType,
} from '../../services/adminSlidersService';

const ACCENT = '#3fced1';

const ASPECT_RATIOS = ['16:9', '4:3', '1:1', '21:9', '3:4', '9:16'];

const inputClasses = {
  inputWrapper: 'bg-zinc-800 border border-zinc-700',
  input: 'text-white',
  label: 'text-gray-400',
};

const selectClasses = {
  trigger: 'bg-zinc-800 border border-zinc-700',
  label: 'text-gray-400',
  value: 'text-white',
};

const FeedbackBanner: React.FC<{
  message: string | null;
  type: 'success' | 'error';
  onClose: () => void;
}> = ({ message, type, onClose }) => {
  if (!message) return null;
  const isSuccess = type === 'success';
  return (
    <div
      className={`mb-4 flex items-start gap-2 rounded-xl border px-4 py-3 text-sm ${
        isSuccess
          ? 'border-[#3fced1]/40 bg-[#3fced1]/10 text-[#3fced1]'
          : 'border-red-500/40 bg-red-500/10 text-red-400'
      }`}
    >
      {isSuccess ? <CheckCircle2 size={18} className="mt-0.5 shrink-0" /> : <XCircle size={18} className="mt-0.5 shrink-0" />}
      <span className="flex-1">{message}</span>
      <button type="button" onClick={onClose} aria-label="Hinweis schließen" className="opacity-70 hover:opacity-100">✕</button>
    </div>
  );
};

/** Formular-Zustand für Anlegen & Bearbeiten eines Slides. */
interface SlideFormState {
  id: string | null; // null = neu anlegen
  type: SlideType;
  order: string;
  title: string;
  subtitle: string;
  linkUrl: string;
  buttonText: string;
  aspectRatio: string;
  sponsorIds: string[];
  /** Neu gewähltes Bild als Data-URL — nur dann wird `image` mitgeschickt. */
  newImageDataUrl: string | null;
  /** Bestehende Bild-URL (nur Anzeige beim Bearbeiten). */
  existingImage: string | null;
}

const emptySlideForm = (type: SlideType): SlideFormState => ({
  id: null,
  type,
  order: '',
  title: '',
  subtitle: '',
  linkUrl: '',
  buttonText: '',
  aspectRatio: '',
  sponsorIds: [],
  newImageDataUrl: null,
  existingImage: null,
});

const AdminSliderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const fileRef = useRef<HTMLInputElement>(null);

  const [slider, setSlider] = useState<AdminSlider | null>(null);
  const [slides, setSlides] = useState<AdminSlide[]>([]);
  const [sponsors, setSponsors] = useState<AdminSponsor[]>([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const [typePickerOpen, setTypePickerOpen] = useState(false);
  const [form, setForm] = useState<SlideFormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [readingImage, setReadingImage] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const [sliderList, slideList, sponsorList] = await Promise.all([
        adminSlidersService.listSliders(),
        adminSlidersService.listSlidesForSlider(id),
        adminSlidersService.listSponsors().catch(() => [] as AdminSponsor[]),
      ]);
      const found = sliderList.find((s) => s.id === id) ?? null;
      if (!found) {
        setError('Slider nicht gefunden.');
      }
      setSlider(found);
      setSlides(slideList);
      setSponsors(sponsorList);
    } catch (err) {
      setError(adminSlidersService.getErrorMessage(err, 'Slider konnte nicht geladen werden.'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const sponsorById = useMemo(() => {
    const map: Record<string, AdminSponsor> = {};
    sponsors.forEach((s) => {
      map[s.id] = s;
    });
    return map;
  }, [sponsors]);

  const openCreate = (type: SlideType) => {
    setTypePickerOpen(false);
    setForm(emptySlideForm(type));
  };

  const openEdit = (slide: AdminSlide) => {
    setForm({
      id: slide.id,
      type: slide.type,
      order: String(slide.order ?? 0),
      title: slide.title ?? '',
      subtitle: slide.subtitle ?? '',
      linkUrl: slide.linkUrl ?? '',
      buttonText: slide.buttonText ?? '',
      aspectRatio: slide.aspectRatio ?? '',
      sponsorIds: [...slide.sponsorIds],
      newImageDataUrl: null,
      existingImage: slide.image,
    });
  };

  const handleImageFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !form) return;
    if (!file.type.startsWith('image/')) {
      setError('Bitte eine Bilddatei auswählen (PNG/JPG/WebP/GIF).');
      return;
    }
    setReadingImage(true);
    try {
      const dataUrl = await adminSlidersService.fileToDataUrl(file);
      setForm((prev) => (prev ? { ...prev, newImageDataUrl: dataUrl } : prev));
    } catch (err) {
      setError('Bild konnte nicht gelesen werden.');
    } finally {
      setReadingImage(false);
    }
  };

  const handleSave = async () => {
    if (!form || !id) return;

    if (form.type === 'manual' && !form.id && !form.newImageDataUrl) {
      setError('Für manuelle Slides ist ein Bild erforderlich.');
      return;
    }
    if (form.type === 'sponsor' && form.sponsorIds.length === 0) {
      setError('Bitte mindestens einen Sponsor auswählen.');
      return;
    }
    const order = form.order.trim() === '' ? null : parseInt(form.order, 10);
    if (order !== null && (isNaN(order) || order < 0)) {
      setError('Reihenfolge muss eine Zahl ≥ 0 sein.');
      return;
    }
    const linkUrl = form.linkUrl.trim();
    if (linkUrl && !/^https?:\/\//i.test(linkUrl)) {
      setError('Die Link-URL muss mit http:// oder https:// beginnen.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload = {
        order,
        title: form.title,
        subtitle: form.subtitle,
        linkUrl,
        buttonText: form.buttonText,
        aspectRatio: form.aspectRatio,
        sponsorIds: form.type === 'sponsor' ? form.sponsorIds : [],
        ...(form.newImageDataUrl ? { image: form.newImageDataUrl } : {}),
      };
      if (form.id) {
        await adminSlidersService.updateSlide(form.id, payload);
        setFeedback('Slide aktualisiert.');
      } else {
        await adminSlidersService.createSlide(id, form.type, payload);
        setFeedback('Slide erstellt.');
      }
      setForm(null);
      await load();
    } catch (err) {
      setError(adminSlidersService.getErrorMessage(err, 'Speichern fehlgeschlagen.'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (slide: AdminSlide) => {
    // Optimistisch umschalten, bei Fehler zurückrollen
    setSlides((prev) => prev.map((s) => (s.id === slide.id ? { ...s, active: !s.active } : s)));
    try {
      await adminSlidersService.toggleSlide(slide.id);
    } catch (err) {
      setSlides((prev) => prev.map((s) => (s.id === slide.id ? { ...s, active: slide.active } : s)));
      setError(adminSlidersService.getErrorMessage(err, 'Status konnte nicht geändert werden.'));
    }
  };

  const handleDelete = async (slide: AdminSlide) => {
    if (confirmDeleteId !== slide.id) {
      setConfirmDeleteId(slide.id);
      return;
    }
    setConfirmDeleteId(null);
    try {
      await adminSlidersService.deleteSlide(slide.id);
      setSlides((prev) => prev.filter((s) => s.id !== slide.id));
      setFeedback('Slide gelöscht.');
    } catch (err) {
      setError(adminSlidersService.getErrorMessage(err, 'Slide konnte nicht gelöscht werden.'));
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  if (!slider) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center">
        <Images size={40} className="mx-auto mb-3 text-zinc-700" />
        <p className="text-gray-400">{error || 'Slider nicht gefunden.'}</p>
        <Button as={RouterLink} to="/admin/slider" className="mt-4 bg-[#3fced1] font-semibold text-black">
          Zurück zur Übersicht
        </Button>
      </div>
    );
  }

  const formPreview = form?.newImageDataUrl ?? adminSlidersService.buildImageUrl(form?.existingImage);

  return (
    <>
      <Helmet>
        <title>{slider.name} | Slider-Admin | DartCloud</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
        {/* Kopf */}
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <Button
            as={RouterLink}
            to="/admin/slider"
            isIconOnly
            variant="bordered"
            className="border-zinc-700 text-gray-300"
            aria-label="Zurück zur Slider-Übersicht"
          >
            <ArrowLeft size={18} />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="flex flex-wrap items-center gap-2 text-xl font-extrabold tracking-tight text-white sm:text-2xl">
              <span className="truncate">{slider.name}</span>
              <span className="shrink-0 rounded-full bg-zinc-800 px-2 py-0.5 font-mono text-xs font-normal text-gray-400">
                {slider.location}
              </span>
              {!slider.active && (
                <span className="shrink-0 rounded-full bg-amber-500/15 px-2 py-0.5 text-xs font-bold text-amber-400">
                  Inaktiv
                </span>
              )}
            </h1>
            {slider.description && <p className="text-xs text-gray-500">{slider.description}</p>}
          </div>
          <Button
            className="bg-[#3fced1] font-semibold text-black"
            startContent={<Plus size={16} />}
            onPress={() => setTypePickerOpen(true)}
          >
            Neuer Slide
          </Button>
        </div>

        <FeedbackBanner message={feedback} type="success" onClose={() => setFeedback(null)} />
        <FeedbackBanner message={error} type="error" onClose={() => setError(null)} />

        {/* Slides */}
        <div className="space-y-3">
          {slides.map((slide) => {
            const imageUrl = adminSlidersService.buildImageUrl(slide.image);
            return (
              <Card key={slide.id} className="border border-zinc-800 bg-zinc-900">
                <CardBody className="flex flex-row flex-wrap items-center gap-3 p-4">
                  {/* Vorschau */}
                  <div className="flex h-16 w-28 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-zinc-700 bg-zinc-950">
                    {slide.type === 'manual' && imageUrl ? (
                      <img src={imageUrl} alt={slide.title ?? ''} className="h-full w-full object-cover" />
                    ) : slide.type === 'sponsor' ? (
                      <Handshake size={20} className="text-[#3fced1]" />
                    ) : (
                      <ImageIcon size={18} className="text-zinc-700" />
                    )}
                  </div>

                  {/* Infos */}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold ${
                          slide.type === 'sponsor'
                            ? 'bg-purple-500/15 text-purple-400'
                            : 'bg-[#3fced1]/10 text-[#3fced1]'
                        }`}
                      >
                        {slide.type === 'sponsor' ? 'Sponsor' : 'Manuell'}
                      </span>
                      <span className="truncate text-sm font-bold text-white">
                        {slide.type === 'sponsor'
                          ? slide.sponsorIds
                              .map((sid) => sponsorById[sid]?.name || 'Unbekannter Sponsor')
                              .join(', ')
                          : slide.title || 'Ohne Titel'}
                      </span>
                      {!slide.active && (
                        <span className="shrink-0 rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-bold text-amber-400">
                          Inaktiv
                        </span>
                      )}
                    </div>
                    <p className="truncate text-xs text-gray-500">
                      Reihenfolge: {slide.order}
                      {slide.aspectRatio && ` · ${slide.aspectRatio}`}
                      {slide.subtitle && ` · ${slide.subtitle}`}
                    </p>
                    {slide.linkUrl && (
                      <p className="flex items-center gap-1 truncate text-xs text-gray-600">
                        <Link2 size={11} className="shrink-0" />
                        <span className="truncate">{slide.linkUrl}</span>
                      </p>
                    )}
                  </div>

                  {/* Aktionen */}
                  <div className="flex shrink-0 items-center gap-2">
                    <Switch
                      size="sm"
                      isSelected={slide.active}
                      onValueChange={() => handleToggle(slide)}
                      aria-label="Slide aktiv"
                    />
                    <Button
                      isIconOnly
                      size="sm"
                      variant="light"
                      aria-label="Slide bearbeiten"
                      className="text-gray-400"
                      onPress={() => openEdit(slide)}
                    >
                      <Pencil size={15} />
                    </Button>
                    <Button
                      isIconOnly
                      size="sm"
                      variant="light"
                      aria-label="Slide löschen"
                      className={confirmDeleteId === slide.id ? 'text-red-400' : 'text-gray-500 hover:text-red-400'}
                      onPress={() => handleDelete(slide)}
                    >
                      <Trash2 size={15} />
                    </Button>
                  </div>
                  {confirmDeleteId === slide.id && (
                    <p className="w-full text-xs text-red-400">
                      Wirklich löschen? Erneut auf den Papierkorb klicken zum Bestätigen.
                    </p>
                  )}
                </CardBody>
              </Card>
            );
          })}

          {slides.length === 0 && (
            <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-zinc-700 py-16 text-center">
              <Images size={40} className="text-zinc-700" />
              <p className="text-gray-500">Noch keine Slides — lege den ersten an!</p>
            </div>
          )}
        </div>
      </div>

      {/* Typ-Auswahl-Modal */}
      <Modal isOpen={typePickerOpen} onClose={() => setTypePickerOpen(false)} placement="center">
        <ModalContent className="border border-zinc-800 bg-zinc-900">
          <ModalHeader className="text-white">Slide-Typ wählen</ModalHeader>
          <ModalBody className="pb-6">
            <button
              type="button"
              onClick={() => openCreate('manual')}
              className="flex items-center gap-3 rounded-xl border border-zinc-700 p-4 text-left transition-colors hover:border-[#3fced1]/60"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#3fced1]/10 text-[#3fced1]">
                <ImageIcon size={20} />
              </span>
              <span>
                <span className="block text-sm font-bold text-white">Manueller Slide</span>
                <span className="block text-xs text-gray-400">
                  Eigenes Bild mit optionalem Titel, Link & Button.
                </span>
              </span>
            </button>
            <button
              type="button"
              onClick={() => openCreate('sponsor')}
              className="flex items-center gap-3 rounded-xl border border-zinc-700 p-4 text-left transition-colors hover:border-purple-500/60"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-purple-500/15 text-purple-400">
                <Handshake size={20} />
              </span>
              <span>
                <span className="block text-sm font-bold text-white">Sponsor-Slide</span>
                <span className="block text-xs text-gray-400">
                  Zeigt einen oder mehrere hinterlegte Sponsoren.
                </span>
              </span>
            </button>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Slide-Formular-Modal (Anlegen & Bearbeiten) */}
      <Modal isOpen={form !== null} onClose={() => setForm(null)} placement="center" scrollBehavior="inside" size="lg">
        <ModalContent className="border border-zinc-800 bg-zinc-900">
          <ModalHeader className="text-white">
            {form?.id ? 'Slide bearbeiten' : form?.type === 'sponsor' ? 'Neuer Sponsor-Slide' : 'Neuer manueller Slide'}
          </ModalHeader>
          {form && (
            <ModalBody>
              {form.type === 'manual' && (
                <>
                  {/* Bild */}
                  <div className="flex items-center gap-3">
                    <div className="flex h-24 w-40 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-zinc-700 bg-zinc-950">
                      {formPreview ? (
                        <img src={formPreview} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <ImageIcon size={20} className="text-zinc-700" />
                      )}
                    </div>
                    <div>
                      <Button
                        size="sm"
                        variant="bordered"
                        className="border-zinc-700 text-gray-300"
                        isLoading={readingImage}
                        startContent={!readingImage && <Upload size={14} />}
                        onPress={() => fileRef.current?.click()}
                      >
                        {formPreview ? 'Bild ersetzen' : 'Bild auswählen'}
                      </Button>
                      {!form.id && (
                        <p className="mt-1 text-xs text-gray-500">Pflichtfeld für manuelle Slides.</p>
                      )}
                    </div>
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImageFile} />
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <Input label="Titel (optional)" value={form.title}
                      onValueChange={(v) => setForm({ ...form, title: v })} classNames={inputClasses} />
                    <Input label="Untertitel (optional)" value={form.subtitle}
                      onValueChange={(v) => setForm({ ...form, subtitle: v })} classNames={inputClasses} />
                    <Input label="Link-URL (optional)" placeholder="https://…" value={form.linkUrl}
                      onValueChange={(v) => setForm({ ...form, linkUrl: v })} classNames={inputClasses} />
                    <Input label="Button-Text (optional)" value={form.buttonText}
                      onValueChange={(v) => setForm({ ...form, buttonText: v })} classNames={inputClasses} />
                    <Select
                      label="Seitenverhältnis (optional)"
                      selectedKeys={form.aspectRatio ? [form.aspectRatio] : []}
                      onSelectionChange={(keys) =>
                        setForm({ ...form, aspectRatio: String(Array.from(keys)[0] ?? '') })
                      }
                      classNames={selectClasses}
                    >
                      {ASPECT_RATIOS.map((r) => (
                        <SelectItem key={r}>{r}</SelectItem>
                      ))}
                    </Select>
                    <Input label="Reihenfolge (optional)" type="number" value={form.order}
                      onValueChange={(v) => setForm({ ...form, order: v })} classNames={inputClasses} />
                  </div>
                </>
              )}

              {form.type === 'sponsor' && (
                <>
                  <p className="text-sm text-gray-400">Sponsoren auswählen (mindestens einen):</p>
                  <div className="max-h-64 space-y-2 overflow-y-auto rounded-xl border border-zinc-800 p-3">
                    {sponsors.map((sponsor) => {
                      const img = adminSlidersService.buildImageUrl(sponsor.image);
                      return (
                        <Checkbox
                          key={sponsor.id}
                          isSelected={form.sponsorIds.includes(sponsor.id)}
                          onValueChange={(checked) =>
                            setForm({
                              ...form,
                              sponsorIds: checked
                                ? [...form.sponsorIds, sponsor.id]
                                : form.sponsorIds.filter((sid) => sid !== sponsor.id),
                            })
                          }
                          classNames={{ base: 'max-w-full', label: 'w-full' }}
                        >
                          <span className="flex items-center gap-2">
                            {img && (
                              <img src={img} alt="" className="h-7 w-7 rounded object-contain" />
                            )}
                            <span className="text-sm text-white">
                              {sponsor.name || `Sponsor ${sponsor.id.substring(0, 8)}`}
                            </span>
                            {!sponsor.active && (
                              <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold text-amber-400">
                                Inaktiv
                              </span>
                            )}
                          </span>
                        </Checkbox>
                      );
                    })}
                    {sponsors.length === 0 && (
                      <p className="py-4 text-center text-sm text-gray-500">
                        Keine Sponsoren vorhanden — lege zuerst Sponsoren in der DartCloud-App an.
                      </p>
                    )}
                  </div>
                  <Input label="Reihenfolge (optional)" type="number" value={form.order}
                    onValueChange={(v) => setForm({ ...form, order: v })} classNames={inputClasses} />
                </>
              )}
            </ModalBody>
          )}
          <ModalFooter>
            <Button variant="light" onPress={() => setForm(null)}>Abbrechen</Button>
            <Button
              isLoading={saving}
              isDisabled={
                !form ||
                (form.type === 'manual' && !form.id && !form.newImageDataUrl) ||
                (form.type === 'sponsor' && form.sponsorIds.length === 0)
              }
              className="bg-[#3fced1] font-semibold text-black"
              onPress={handleSave}
            >
              {form?.id ? 'Speichern' : 'Anlegen'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default AdminSliderDetailPage;
